/* eslint-disable max-lines */
import {
  BookabilityStatus,
  type Offer,
  type OpenShift,
  type Workplace,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { addHours, differenceInHours, parseISO, subHours } from "date-fns";

import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { MANDATORY_BREAK_DURATION_IN_MINUTES } from "../constants";
import { doShiftsExcessivelyOverlap } from "./doShiftsExcessivelyOverlap";
import type {
  BookedShiftWithContext,
  BundleShiftPay,
  OpenBundleShift,
  ShiftBundle,
  ShiftBundlesConfig,
  ShiftTimeData,
  ShiftWithOffer,
} from "./types";
import {
  OVERWORKING_WINDOW_SIZE_IN_HOURS,
  wouldProposedShiftCauseOverworking,
} from "./wouldProposedShiftCauseOverworking";

export function getBundleLabel(requiredShiftCount: number, totalShiftCount: number): string {
  if (requiredShiftCount <= 1) {
    return "Shift Bundle";
  }

  return requiredShiftCount === totalShiftCount
    ? `${totalShiftCount} shift bundle`
    : `${requiredShiftCount}+ shift bundle`;
}

function createBundleId(workplaceId: string, qualification: string): string {
  return `bundle-${workplaceId}-${qualification}`;
}

export function calculateRequiredShiftCount({
  availableShiftCount,
  config,
  bookedShifts,
  workplaceId,
  recentShiftCount = 0,
}: {
  availableShiftCount: number;
  config: Extract<ShiftBundlesConfig, { enabled: true }>;
  bookedShifts: BookedShiftWithContext[];
  workplaceId: string;
  recentShiftCount?: number;
}): number {
  const { percentageOfAvailableShiftsRequiredToBundle, maxRequiredShifts, minimumBundleSize } =
    config;
  const raw = Math.ceil(availableShiftCount * percentageOfAvailableShiftsRequiredToBundle);
  const baseRequiredShiftCount = Math.min(Math.max(raw, minimumBundleSize), maxRequiredShifts);

  const alreadyBookedCount = bookedShifts.filter(
    (shift) => shift.workplaceId === workplaceId
  ).length;

  const remainingRequiredShiftCount =
    baseRequiredShiftCount - alreadyBookedCount - recentShiftCount;
  return Math.max(1, remainingRequiredShiftCount);
}

export function isShiftWithOffer(shift: OpenShift): shift is ShiftWithOffer {
  return isDefined(shift.relationships.offer?.data.id);
}

interface ShiftBundleEligibilityParams {
  leadTimeInHours: number;
  bookedShifts: ShiftTimeData[];
}

/**
 * Checks if a shift is eligible for bundling based on:
 * 1. Has an associated offer
 * 2. Starts after the lead time threshold
 * 3. Is bookable (not windowed out)
 * 4. Would not cause overworking when combined with booked shifts
 */
function isShiftEligibleForBundle(
  shift: OpenShift,
  params: ShiftBundleEligibilityParams
): shift is ShiftWithOffer {
  const { leadTimeInHours, bookedShifts } = params;
  const now = new Date();

  if (!isShiftWithOffer(shift)) {
    return false;
  }

  const shiftStart = parseISO(shift.attributes.start);
  if (differenceInHours(shiftStart, now) < leadTimeInHours) {
    return false;
  }

  if (shift.attributes.bookability?.status !== BookabilityStatus.BOOKABLE) {
    return false;
  }

  const shiftData = {
    start: shift.attributes.start,
    end: shift.attributes.end,
  };
  if (wouldProposedShiftCauseOverworking(shiftData, bookedShifts)) {
    return false;
  }

  return true;
}

function calculateShiftPayFromOffer(
  shift: ShiftWithOffer,
  offersMap: Map<string, Offer>,
  requiresLunchBreak: boolean
): BundleShiftPay {
  const offerId = shift.relationships.offer.data.id;
  const offer = offersMap.get(offerId);

  if (!isDefined(offer)) {
    return { totalPayInMinorUnits: 0, hourlyPayInMinorUnits: 0 };
  }

  const hourlyPayInMinorUnits = offer.attributes.pay.amountInMinorUnits;
  const workDurationInHours = getShiftWorkDurationInHours(
    { start: shift.attributes.start, end: shift.attributes.end },
    requiresLunchBreak,
    MANDATORY_BREAK_DURATION_IN_MINUTES
  );

  return {
    totalPayInMinorUnits: Math.round(workDurationInHours * hourlyPayInMinorUnits),
    hourlyPayInMinorUnits,
  };
}

function attachPayToShifts(
  shifts: ShiftWithOffer[],
  offersMap: Map<string, Offer>,
  requiresLunchBreak: boolean
): OpenBundleShift[] {
  return shifts.map((shift) => ({
    ...shift,
    pay: calculateShiftPayFromOffer(shift, offersMap, requiresLunchBreak),
  }));
}

function calculateBundleTotalPayInMinorUnits(
  bundleShifts: ShiftWithOffer[],
  offersMap: Map<string, Offer>,
  requiresLunchBreak: boolean
): number {
  let totalMinorUnits = 0;

  for (const shift of bundleShifts) {
    const pay = calculateShiftPayFromOffer(shift, offersMap, requiresLunchBreak);
    totalMinorUnits += pay.totalPayInMinorUnits;
  }

  return totalMinorUnits;
}

/**
 * Finds compatible companion shifts for a given shift.
 * Companions must not cause overworking or excessively overlap with the shift.
 */
function findCompatibleCompanionShifts(
  anchorShift: ShiftWithOffer,
  candidateShifts: ShiftWithOffer[],
  bookedShifts: ShiftTimeData[]
): ShiftWithOffer[] {
  const anchorData = {
    start: anchorShift.attributes.start,
    end: anchorShift.attributes.end,
  };

  return candidateShifts.filter((companionShift) => {
    if (companionShift.id === anchorShift.id) {
      return true; // Always include the anchor shift itself
    }

    const companionData = {
      start: companionShift.attributes.start,
      end: companionShift.attributes.end,
    };

    const anchorStart = parseISO(anchorData.start);
    const anchorEnd = parseISO(anchorData.end);
    const companionStart = parseISO(companionData.start);
    const companionEnd = parseISO(companionData.end);
    const windowEnd = addHours(anchorStart, OVERWORKING_WINDOW_SIZE_IN_HOURS);
    const windowStart = subHours(anchorEnd, OVERWORKING_WINDOW_SIZE_IN_HOURS);

    // If companion starts after anchor's forward 24-hour window ends, it's compatible
    if (companionStart >= windowEnd) {
      return true;
    }

    // If companion ends before anchor's backward 24-hour window starts, it's compatible
    if (companionEnd <= windowStart) {
      return true;
    }

    // Exclude companions that overlap with the anchor shift by more than 30 minutes
    if (doShiftsExcessivelyOverlap(anchorData, companionData)) {
      return false;
    }

    // Otherwise, check if adding companion to (anchor + booked shifts) would cause overworking
    return !wouldProposedShiftCauseOverworking(companionData, [anchorData, ...bookedShifts]);
  });
}

/**
 * Gets the bundle for a single specific shift.
 *
 * @param targetShiftId - The ID of the shift to get the bundle for
 * @param workplaceShifts - All shifts at the same workplace with the same qualification
 * @param config - Bundle configuration from feature flag
 * @param bookedShifts - Worker's currently booked shifts
 * @param offersMap - Map of offer IDs to offers for pay calculation
 * @param requiresLunchBreak - Whether the workplace requires lunch breaks (affects pay calculation)
 * @returns The bundle for the target shift, or undefined if not eligible
 */
export function getBundleForShift({
  targetShiftId,
  workplaceShifts,
  config,
  bookedShifts,
  offersMap = new Map(),
  requiresLunchBreak,
  recentShiftCount = 0,
}: {
  targetShiftId: string;
  workplaceShifts: OpenShift[];
  config: ShiftBundlesConfig;
  bookedShifts: BookedShiftWithContext[];
  offersMap?: Map<string, Offer>;
  requiresLunchBreak: boolean;
  recentShiftCount?: number;
}): ShiftBundle | undefined {
  if (!config.enabled) {
    return undefined;
  }

  const { leadTimeInHours, minimumBundleSize } = config;

  const targetShift = workplaceShifts.find((shift) => shift.id === targetShiftId);
  if (!targetShift) {
    return undefined;
  }

  if (!isShiftEligibleForBundle(targetShift, { leadTimeInHours, bookedShifts })) {
    return undefined;
  }

  const workplaceId = targetShift.relationships.workplace.data.id;
  const { qualification } = targetShift.attributes;

  const eligibleShifts = workplaceShifts.filter((shift): shift is ShiftWithOffer => {
    if (shift.attributes.qualification !== qualification) {
      return false;
    }

    return isShiftEligibleForBundle(shift, { leadTimeInHours, bookedShifts });
  });

  if (eligibleShifts.length < minimumBundleSize) {
    return undefined;
  }

  const compatibleShifts = findCompatibleCompanionShifts(targetShift, eligibleShifts, bookedShifts);

  if (compatibleShifts.length < minimumBundleSize) {
    return undefined;
  }

  const shiftsWithPay = attachPayToShifts(compatibleShifts, offersMap, requiresLunchBreak);

  const requiredShiftCount = calculateRequiredShiftCount({
    availableShiftCount: shiftsWithPay.length,
    config,
    bookedShifts,
    workplaceId,
    recentShiftCount,
  });

  return {
    workplaceId,
    qualification,
    shifts: shiftsWithPay,
    totalShiftCount: shiftsWithPay.length,
    requiredShiftCount,
    totalPayInMinorUnits: calculateBundleTotalPayInMinorUnits(
      compatibleShifts,
      offersMap,
      requiresLunchBreak
    ),
  };
}

/**
 * Groups shifts into bundles by (workplaceId, qualification), filtering out shifts that
 * would cause overworking (>17 hours within any 24-hour window) or excessively overlap
 * (>30 minutes of overlap).
 *
 * A shift is eligible for bundling if:
 * 1. Its workplace is in the `eligibleWorkplaceIds` set
 * 2. It starts more than `leadTimeInHours` from now
 * 3. It is not windowed out
 * 4. It doesn't cause overworking when combined with booked shifts
 *
 * Each shift gets its own bundle with filtered companion shifts (excluding those that
 * would cause overworking if booked together or that excessively overlap with the anchor
 * shift). A shift only gets a bundle if it has at least `minimumBundleSize` compatible
 * companions.
 *
 * Returns a map of shiftId → ShiftBundle.
 */
export function groupShiftsIntoBundles({
  shifts,
  config,
  eligibleWorkplaceIds,
  recentWorkplaceWorkedShiftCounts = new Map<string, number>(),
  bookedShifts,
  offersMap = new Map(),
  workplacesMap = new Map(),
}: {
  shifts: OpenShift[];
  config: ShiftBundlesConfig;
  eligibleWorkplaceIds: Set<string>;
  recentWorkplaceWorkedShiftCounts?: Map<string, number>;
  bookedShifts: BookedShiftWithContext[];
  offersMap?: Map<string, Offer>;
  workplacesMap?: Map<string, Workplace>;
}): Map<string, ShiftBundle> {
  if (!config.enabled) {
    return new Map();
  }

  const { leadTimeInHours, minimumBundleSize } = config;
  const eligibilityParams = { leadTimeInHours, bookedShifts };

  const groups = new Map<
    string,
    { workplaceId: string; qualification: string; shifts: ShiftWithOffer[] }
  >();

  for (const shift of shifts) {
    const workplaceId = shift.relationships.workplace.data.id;

    if (!eligibleWorkplaceIds.has(workplaceId)) {
      continue;
    }

    if (!isShiftEligibleForBundle(shift, eligibilityParams)) {
      continue;
    }

    const { qualification } = shift.attributes;
    const key = createBundleId(workplaceId, qualification);

    const existing = groups.get(key);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      groups.set(key, { workplaceId, qualification, shifts: [shift] });
    }
  }

  for (const [bundleId, group] of groups) {
    if (group.shifts.length < minimumBundleSize) {
      groups.delete(bundleId);
    }
  }

  const bundlesByShiftId = new Map<string, ShiftBundle>();

  for (const group of groups.values()) {
    for (const shift of group.shifts) {
      const compatibleShifts = findCompatibleCompanionShifts(shift, group.shifts, bookedShifts);

      // Only create bundle if it meets minimum size requirement
      if (compatibleShifts.length < minimumBundleSize) {
        // Skip this shift - it doesn't have enough compatible companions
        // But continue processing other shifts in the group
        continue;
      }

      const workplace = workplacesMap.get(group.workplaceId);
      const requiresLunchBreak = workplace?.attributes.requiresLunchBreak ?? false;

      const shiftsWithPay = attachPayToShifts(compatibleShifts, offersMap, requiresLunchBreak);

      const requiredShiftCount = calculateRequiredShiftCount({
        availableShiftCount: shiftsWithPay.length,
        config,
        bookedShifts,
        workplaceId: group.workplaceId,
        recentShiftCount: recentWorkplaceWorkedShiftCounts.get(group.workplaceId) ?? 0,
      });

      const bundle: ShiftBundle = {
        workplaceId: group.workplaceId,
        qualification: group.qualification,
        shifts: shiftsWithPay,
        totalShiftCount: shiftsWithPay.length,
        requiredShiftCount,
        totalPayInMinorUnits: calculateBundleTotalPayInMinorUnits(
          compatibleShifts,
          offersMap,
          requiresLunchBreak
        ),
      };

      bundlesByShiftId.set(shift.id, bundle);
    }
  }

  return bundlesByShiftId;
}
/* eslint-enable max-lines */
